import * as angular from 'angular';

import { StorageService } from '../services/storage-service';

let nextTabsId = Date.now();

interface ITabsPanelMap {
    [id: string]: {
        title: JQuery;
        content: JQuery;
    };
}

interface ITabsScope extends angular.IScope {
    key: string;
    uid: string;
    change: () => void;
}

class TabsController implements ITabsController {
    static readonly $inject = ['$scope', '$element', 'fcStorageService'];

    private nextPanelId = 1;
    private activeId = `${1}`;
    private readonly panels: ITabsPanelMap = {};

    constructor(
        private readonly scope: ITabsScope,
        private readonly element: JQuery,
        private readonly storageService: StorageService
    ) {
        // Get the stored panel id if there is one
        if (this.scope.key) {
            const activeId = this.storageService.get(`${this.scope.key}__tabs`);

            if (activeId) {
                this.activeId = activeId;
            }
        }

        // Hook the accordion down event to control the element classes
        this.element.on('change.zf.tabs', (_event, trigger) => {
            const id = trigger.index() + 1;
            this.activatePanel(`${id}`);

            // Emit an event when the active panel changes
            if (this.scope.change) {
                this.scope.change();
            }
        });

        this.scope.$on('$destroy', () => {
            this.element.off();
        });
    }

    registerPanel(element: JQuery): void {
        const id = `${this.nextPanelId++}`;
        this.panels[id] = {
            title: element.find('.fc-tabs-title'),
            content: element.find('.fc-tabs-content')
        };

        // Set the active id if the panel is registering as active
        if (id === this.activeId) {
            this.activatePanel(this.activeId);
        }
    }

    ensurePanelActive(): void {
        const ids = Object.keys(this.panels);
        const panel = this.panels[this.activeId];

        // If no panel is active, just pick the first one
        if (!panel && ids.length > 0) {
            this.activeId = ids[0];
            this.activatePanel(this.activeId);
        }
    }

    private activatePanel(activeId: string): void {
        // Cycle the panels and adjust the active classes
        Object.keys(this.panels).forEach(id => {
            const panel = this.panels[id];

            if (id === activeId) {
                panel.title.addClass(['fc-tabs-active', 'is-active']);
                panel.content.addClass(['fc-tabs-active', 'is-active']);
            } else {
                panel.title.removeClass(['fc-tabs-active', 'is-active']);
                panel.content.removeClass(['fc-tabs-active', 'is-active']);
            }
        });

        // Then record the active id is a key is set
        if (this.scope.key) {
            this.storageService.set(`${this.scope.key}__tabs`, activeId);
        }
    }
}

class TabsDirective implements angular.IDirective {
    readonly replace = true;
    readonly transclude = true;
    readonly restrict = 'E';
    readonly controller = TabsController;
    readonly scope = {
        key: '@',
        change: '&?change'
    };

    readonly template = `
        <div class="fc-tabs">
            <ul id="{{ uid }}" class="fc-tabs-titles tabs" layout="row" data-tabs>
            </ul>
            <div class="fc-tabs-contents tabs-content" data-tabs-content="{{ uid }}">
            </div>
            <div class="fc-tabs-transclude" ng-transclude></div>
        </div>
    `;

    readonly link: angular.IDirectiveLinkFn;

    constructor() {
        this.link = this._link.bind(this);
    }

    private _link(
        scope: ITabsScope,
        element: JQuery,
        _attributes: angular.IAttributes,
        controller: TabsController
    ): void {
        // Create a unique id to identify this tabs instance (in case there are multiples)
        const uid = `fc-tabs__${nextTabsId++}`;
        scope.uid = uid;

        // Need to restructure the html to match the foundation sites component
        const titleContainer = element.find('.fc-tabs-titles');
        const contentContainer = element.find('.fc-tabs-contents');
        const transcludeElement = element.find('.fc-tabs-transclude');
        const titleElements = transcludeElement.find('.fc-tabs-title');
        const contentElements = transcludeElement.find('.fc-tabs-content');

        for (let index = 0; index < titleElements.length; index++) {
            const panelUid = `${uid}_${index}`;
            const titleElement = titleElements.eq(index);
            const contentElement = contentElements.eq(index);

            // Attach the tab panel link to the content
            titleElement.find('a').attr('href', `#${panelUid}`);
            contentElement.attr('id', panelUid);

            // Move the title and content elements to the relevant containers
            titleElement.appendTo(titleContainer);
            contentElement.appendTo(contentContainer);
        }

        // Probably don't need to, but tidy up the transclude element - it's not needed
        transcludeElement.remove();

        // Always ensure a panel is active
        controller.ensurePanelActive();
    }
}

export interface ITabsController {
    registerPanel(element: JQuery): void;
}

const TabsDirectiveFactory: angular.IDirectiveFactory = () => new TabsDirective();

export { TabsDirectiveFactory };
